import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { CONSENT_EVENT, consentKey, clearMemoryConsent, connectEmbeddedConsent, readConsent, saveConsent } from '../../lib/tracking/consent';
import { fetchTrackingConfig, mountTracking } from '../../lib/tracking/runtime';

export default function PracticeTracking({ orgSlug }) {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [choices, setChoices] = useState({ analytics: false, marketing: false });
  const [generation, setGeneration] = useState(0);
  const embedded = window.parent !== window;
  useEffect(() => {
    let active = true;
    setConfig(null);
    if (!orgSlug) return;
    fetchTrackingConfig(orgSlug).then((value) => {
      if (!active || !value.organization_id || !value.revision) return;
      setConfig(value);
      const consent = readConsent(value);
      setChoices({ analytics: consent?.analytics === true, marketing: consent?.marketing === true });
      setOpen(!consent);
    }).catch(() => {});
    return () => { active = false; };
  }, [orgSlug, embedded]);

  useEffect(() => {
    if (!config) return;
    const cleanup = mountTracking(config, orgSlug);
    const changed = (event) => {
      if (event.detail?.organization_id === config.organization_id) {
        cleanup(); // Stop vendor listeners synchronously on revocation.
        setGeneration((value) => value + 1);
      }
    };
    window.addEventListener(CONSENT_EVENT, changed);
    const storageChanged = (event) => {
      if (event.key && event.key !== consentKey(config)) return;
      cleanup();
      clearMemoryConsent(config);
      const consent = readConsent(config);
      setChoices({ analytics: consent?.analytics === true, marketing: consent?.marketing === true });
      setOpen(!consent);
      setGeneration((value) => value + 1);
    };
    window.addEventListener('storage', storageChanged);
    return () => { cleanup(); window.removeEventListener(CONSENT_EVENT, changed); window.removeEventListener('storage', storageChanged); };
  }, [config, orgSlug, generation]);

  useEffect(() => {
    if (!config || !embedded) return undefined;
    return connectEmbeddedConsent(config);
  }, [config, embedded]);

  if (!config || embedded || !(config.ga4_enabled || config.google_ads_enabled || config.meta_enabled)) return null;
  const accept = (next) => { saveConsent(config, next); setChoices(next); setOpen(false); };
  const advertising = config.google_ads_enabled || config.meta_enabled;
  const vendors = [config.ga4_enabled && 'Google Analytics', config.google_ads_enabled && 'Google Ads', config.meta_enabled && 'Meta'].filter(Boolean).join(', ');
  if (!open) return (
    <button
      type="button"
      onClick={() => { setDetails(true); setOpen(true); }}
      aria-label="Cookie-Einstellungen öffnen"
      title="Cookie-Einstellungen"
      className="fixed bottom-0 left-4 z-[110] h-2 w-12 rounded-t-full border-x border-t border-gray-300 bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.16)] transition-all hover:h-3 hover:w-14 focus-visible:h-3 focus-visible:w-14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
    >
      <span className="sr-only">Cookie-Einstellungen öffnen</span>
    </button>
  );
  return (
    <section aria-label="Cookie-Einstellungen dieser Praxis" className="fixed inset-x-2 bottom-2 z-[150] mx-auto max-h-[85dvh] max-w-2xl overflow-y-auto rounded-2xl border bg-white p-4 shadow-xl sm:p-6">
      <h2 className="font-semibold">Ihre Cookie-Einstellungen</h2>
      <p className="mt-2 text-sm text-gray-600">{config.organization_name || 'Diese Praxis'} möchte mit {vendors} messen, ob Anfragen und Buchungen über Werbung entstehen. Dabei werden Daten an die ausgewählten Anbieter übermittelt. Sie entscheiden freiwillig. Ihre Buchung oder Anfrage funktioniert auch ohne Zustimmung.</p>
      {details && <div className="my-4 space-y-4">
        {config.ga4_enabled && <label className="flex items-center justify-between gap-4 text-sm"><span>Analyse mit Google Analytics</span><Switch aria-label="Analyse erlauben" checked={choices.analytics} onCheckedChange={(value) => setChoices((old) => ({ ...old, analytics: value }))} /></label>}
        {advertising && <label className="flex items-center justify-between gap-4 text-sm"><span>Erfolgsmessung mit {config.google_ads_enabled && config.meta_enabled ? 'Google Ads und Meta' : config.meta_enabled ? 'Meta' : 'Google Ads'}</span><Switch aria-label="Werbe-Erfolgsmessung erlauben" checked={choices.marketing} onCheckedChange={(value) => setChoices((old) => ({ ...old, marketing: value }))} /></label>}
      </div>}
      <a href={config.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm text-blue-700 underline">Datenschutz der Praxis</a>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <Button variant="outline" className="h-auto whitespace-normal py-2" onClick={() => accept({ analytics: false, marketing: false })}>Ablehnen</Button>
        <Button variant="outline" className="h-auto whitespace-normal py-2" onClick={() => details ? accept(choices) : setDetails(true)}>{details ? 'Auswahl speichern' : 'Anpassen'}</Button>
        <Button className="h-auto whitespace-normal py-2 shadow-sm" onClick={() => accept({ analytics: config.ga4_enabled === true, marketing: advertising === true })}>Allen zustimmen</Button>
      </div>
    </section>
  );
}
